import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCalendarAlt, FaClock } from 'react-icons/fa';

const getExcerpt = (content = '') => {
  const plainText = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return plainText.length > 130 ? `${plainText.slice(0, 130)}...` : plainText;
};

const BlogCard = ({ blog }) => {
  if (!blog) return null;

  const {
    title = '',
    shortDescription,
    author,
    authorName,
    createdAt,
    featuredImage,
    slug,
    content = '',
    readingTime = 3,
    category,
    tags = [],
  } = blog;

  const authorDisplayName = author?.name || authorName || 'Finegal Team';
  const authorInitial = authorDisplayName.charAt(0).toUpperCase();

  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : 'Recent';

  const description = shortDescription || getExcerpt(content);

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs transition-all duration-300 hover:-translate-y-1 hover:border-amber-300 hover:shadow-xl">
      {/* Featured Image Container */}
      <Link to={`/blogs/${slug}`} className="relative block h-52 w-full overflow-hidden bg-slate-100">
        {featuredImage ? (
          <img
            src={featuredImage}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-slate-400">
            <span className="text-sm font-semibold tracking-wider uppercase">Finegal Insights</span>
          </div>
        )}

        {/* Gradient shadow overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        {/* Floating Category Pill */}
        {category && (
          <span className="absolute top-3 left-3 rounded-full bg-amber-500/95 px-3 py-1 text-xs font-semibold text-white shadow-sm backdrop-blur-xs">
            {category}
          </span>
        )}

        {/* Reading Time Badge */}
        <span className="absolute top-3 right-3 flex items-center gap-1 rounded-full bg-slate-900/70 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm backdrop-blur-xs">
          <FaClock className="text-[10px] text-amber-400" />
          <span>{readingTime || 3} min</span>
        </span>
      </Link>

      {/* Card Content */}
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        {/* Date and Tags */}
        <div className="mb-2.5 flex items-center gap-2 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <FaCalendarAlt className="text-slate-400 text-[11px]" />
            {formattedDate}
          </span>
          {tags && tags.length > 0 && (
            <>
              <span>•</span>
              <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                #{tags[0]}
              </span>
            </>
          )}
        </div>

        {/* Title */}
        <h3 className="mb-2.5 text-lg font-bold tracking-tight text-slate-900 transition-colors duration-200 group-hover:text-amber-600 line-clamp-2">
          <Link to={`/blogs/${slug}`} className="hover:underline">
            {title}
          </Link>
        </h3>

        {/* Excerpt */}
        <p className="mb-6 flex-1 text-sm leading-relaxed text-slate-600 line-clamp-3">
          {description || 'Click to read full article insights and takeaways.'}
        </p>

        {/* Card Footer: Author + Read More */}
        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-4">
          {/* Author avatar & name */}
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#01295c] to-[#2596be] text-xs font-bold text-white shadow-xs">
              {authorInitial}
            </div>
            <div className="text-xs">
              <p className="font-semibold text-slate-800">{authorDisplayName}</p>
              <p className="text-[10px] text-slate-400">Advisory</p>
            </div>
          </div>

          {/* Read article CTA */}
          <Link
            to={`/blogs/${slug}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#01295c] transition-colors group-hover:text-amber-600"
          >
            Read Article
            <FaArrowRight className="text-[10px] transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
